/**
 * Auth Utility Functions (Shared)
 *
 * Platform-wide helper functions for authentication.
 *
 * @module lib/api/auth/utils
 */

// ─────────────────────────────────────────────────────────────────────────────
// Token Management
// ─────────────────────────────────────────────────────────────────────────────

/** Storage keys for auth tokens */
export const AUTH_STORAGE_KEYS = {
  ACCESS_TOKEN: 'amalq_user_access_token',
  REFRESH_TOKEN: 'amalq_user_refresh_token',
  TOKEN_EXPIRY: 'amalq_user_token_expiry',
  USER: 'user',
  IS_AUTHENTICATED: 'isAuthenticated',
  // Legacy Gazian key (for cleanup)
  GAZIAN_SESSION: 'gazian_customer_session',
} as const;

/**
 * Get access token from localStorage
 *
 * @returns JWT access token or null if not authenticated
 */
export const getAccessToken = (): string | null =>
  localStorage.getItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN);

/**
 * Get refresh token from localStorage
 *
 * @returns JWT refresh token or null if not set
 */
export const getRefreshToken = (): string | null =>
  localStorage.getItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN);

/**
 * Store auth tokens in localStorage
 *
 * @param accessToken - JWT access token
 * @param refreshToken - JWT refresh token
 * @param expiresIn - Token expiry in seconds (optional)
 */
export const storeAuthTokens = (
  accessToken: string,
  refreshToken: string,
  expiresIn?: number
): void => {
  localStorage.setItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN, accessToken);
  localStorage.setItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN, refreshToken);

  if (expiresIn) {
    const expiryTime = Date.now() + expiresIn * 1000;
    localStorage.setItem(AUTH_STORAGE_KEYS.TOKEN_EXPIRY, expiryTime.toString());
  }
};

/**
 * Clear all auth tokens from localStorage
 * Also clears legacy Gazian session if present
 */
export const clearAuthTokens = (): void => {
  localStorage.removeItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN);
  localStorage.removeItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN);
  localStorage.removeItem(AUTH_STORAGE_KEYS.TOKEN_EXPIRY);
  localStorage.removeItem(AUTH_STORAGE_KEYS.USER);
  localStorage.removeItem(AUTH_STORAGE_KEYS.IS_AUTHENTICATED);
  // Clear legacy Gazian session
  localStorage.removeItem(AUTH_STORAGE_KEYS.GAZIAN_SESSION);
};

/**
 * Check if user is authenticated (has valid access token)
 *
 * @returns boolean indicating if user has access token
 */
export const isAuthenticated = (): boolean => {
  const token = getAccessToken();
  if (!token) return false;

  // Optionally check token expiry
  const expiry = localStorage.getItem(AUTH_STORAGE_KEYS.TOKEN_EXPIRY);
  if (expiry) {
    const expiryTime = parseInt(expiry, 10);
    if (Date.now() >= expiryTime) {
      // Token expired
      return false;
    }
  }

  return true;
};

// ─────────────────────────────────────────────────────────────────────────────
// Date Formatting
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Format estimated delivery date for display
 *
 * Handles:
 * - ISO date strings (converts to readable format)
 * - Date ranges (passes through)
 * - Pre-formatted strings (passes through)
 *
 * @param delivery - Delivery date string (ISO, range, or formatted)
 * @returns Formatted date string or null if undefined
 */
export const formatEstimatedDelivery = (
  delivery: string | undefined
): string | null => {
  if (!delivery) return null;

  // Already formatted as a range or US date format - pass through
  if (delivery.includes(' - ') || delivery.includes('/')) {
    return delivery;
  }

  // Try to parse as ISO date
  const date = new Date(delivery);
  if (isNaN(date.getTime())) {
    // Not a valid date, return as-is
    return delivery;
  }

  // Format as readable date
  return date.toLocaleDateString('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });
};

// ─────────────────────────────────────────────────────────────────────────────
// OTP Constants
// ─────────────────────────────────────────────────────────────────────────────

/** OTP configuration constants */
export const OTP_CONFIG = {
  /** Number of digits in OTP code */
  CODE_LENGTH: 6,
  /** Cooldown period before resend is allowed (in seconds) */
  RESEND_COOLDOWN_SECONDS: 60,
  /** OTP expiry time (in seconds) - should match backend */
  EXPIRY_SECONDS: 300,
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// Return URL Management
// ─────────────────────────────────────────────────────────────────────────────

/** Session storage key for return URL after login */
const RETURN_URL_KEY = 'amalq_auth_return_url';

/**
 * Store return URL for post-login redirect
 *
 * @param url - URL to redirect to after successful login
 */
export const setReturnUrl = (url: string): void => {
  sessionStorage.setItem(RETURN_URL_KEY, url);
};

/**
 * Get and clear return URL
 *
 * @returns URL to redirect to, or null if not set
 */
export const getAndClearReturnUrl = (): string | null => {
  const url = sessionStorage.getItem(RETURN_URL_KEY);
  if (url) {
    sessionStorage.removeItem(RETURN_URL_KEY);
  }
  return url;
};
